// The editable fields of a vehicle, grouped as the card and the new-vehicle page show them.
import { sv } from '@aldorock-fordon/shared/sv';
import type { VehicleCard } from '@aldorock-fordon/shared/api-types';

export type V = VehicleCard['vehicle'];
export type FieldKey = keyof typeof sv.vehicleCard.fields;
export type FieldKind = 'text' | 'int' | 'date' | 'select';

export type FieldDef = {
  key: FieldKey;
  kind: FieldKind;
  options?: Record<string, string>;
  readOnly?: boolean;
};

export const REGISTER_FIELDS: FieldDef[] = [
  { key: 'registrationNumber', kind: 'text' },
  { key: 'vin', kind: 'text' },
  { key: 'status', kind: 'select', options: sv.vehicleStatus },
  { key: 'vehicleType', kind: 'select', options: sv.vehicleTypes },
  { key: 'make', kind: 'text' },
  { key: 'model', kind: 'text' },
  { key: 'modelYear', kind: 'int' },
  { key: 'colour', kind: 'text' },
  { key: 'fuel', kind: 'select', options: sv.fuels },
  { key: 'co2GKm', kind: 'int' },
  { key: 'emissionClass', kind: 'text' },
  { key: 'seats', kind: 'int' },
  { key: 'kerbWeightKg', kind: 'int' },
  { key: 'totalWeightKg', kind: 'int' },
  { key: 'firstRegistered', kind: 'date' },
  { key: 'inspectionLast', kind: 'date' },
  { key: 'inspectionDue', kind: 'date' },
  { key: 'taxAnnualSek', kind: 'int' },
  { key: 'taxDueMonth', kind: 'int' },
  { key: 'odometerKm', kind: 'int' },
];

export const ORG_FIELDS: FieldDef[] = [
  { key: 'managerName', kind: 'text' },
  { key: 'coordinatorName', kind: 'text' },
  { key: 'representativeName', kind: 'text' },
  { key: 'reference', kind: 'text' },
  { key: 'externalId', kind: 'text' },
];

export const PLACE_FIELDS: FieldDef[] = [
  { key: 'parking', kind: 'text' },
  { key: 'charger', kind: 'text' },
];

export const ECONOMY_FIELDS: FieldDef[] = [
  { key: 'ownership', kind: 'select', options: sv.ownership },
  { key: 'lessor', kind: 'text' },
  { key: 'leaseStart', kind: 'date' },
  { key: 'leaseEnd', kind: 'date' },
  { key: 'leaseMonthlySek', kind: 'int' },
  { key: 'acquiredOn', kind: 'date' },
  { key: 'acquisitionSek', kind: 'int' },
  { key: 'dimAnsvar', kind: 'text' },
  { key: 'dimTjanst', kind: 'text' },
  { key: 'dimAktivitet', kind: 'text' },
  { key: 'dimObjekt', kind: 'text' },
  { key: 'dimMotpart', kind: 'text' },
  { key: 'codeStrings', kind: 'text' },
];

export type Draft = Record<string, string>;

export function toDraft(v: Partial<V>): Draft {
  const d: Draft = {};
  for (const f of [...REGISTER_FIELDS, ...ORG_FIELDS, ...PLACE_FIELDS, ...ECONOMY_FIELDS]) {
    const val = v[f.key as keyof V];
    d[f.key] = val === null || val === undefined ? '' : String(val);
  }
  return d;
}

export function fromDraft(d: Draft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of [...REGISTER_FIELDS, ...ORG_FIELDS, ...PLACE_FIELDS, ...ECONOMY_FIELDS]) {
    const raw = (d[f.key] ?? '').trim();
    if (
      f.key === 'registrationNumber' ||
      f.key === 'vehicleType' ||
      f.key === 'status' ||
      f.key === 'ownership'
    ) {
      if (raw) out[f.key] = raw;
      continue;
    }
    if (!raw) out[f.key] = null;
    else if (f.kind === 'int') out[f.key] = Number(raw);
    else out[f.key] = raw;
  }
  return out;
}

export function display(f: FieldDef, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (f.kind === 'select' && f.options) return f.options[String(value)] ?? String(value);
  if (f.kind === 'int') return new Intl.NumberFormat('sv-SE').format(Number(value));
  return String(value);
}
